'use client'
import React, { createContext, useEffect, useReducer } from 'react'
import {
  CartReducer,
  initialState,
  actionType,
  initialStateType,
} from './CartReducer'
import cartService from '../../cart'

type AppThunk<ReturnType = void> = (
  dispatch: (action: actionType) => void,
  state: initialStateType,
) => Promise<ReturnType>

export type DispatchType = <T = void>(
  action: actionType | AppThunk<T>
) => Promise<T> | void

export type CartContextValue = initialStateType & {
  updateCurrency: (currency: string) => void
}

export const CartContext = createContext<CartContextValue>({
  ...initialState,
  updateCurrency: () => {},
})

export const CartDispatchContext = createContext<DispatchType | null>(null)

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [cart, _dispatch] = useReducer(CartReducer, initialState)

  //useEffect in CartContext to prevent auto-sync during country change
  useEffect(() => {
    let isChangingCountry = false;
    let syncTimeout: NodeJS.Timeout;

    const handleLocaleChanged = () => {
      console.log('[CartProvider] Country change detected, pausing auto-sync');
      isChangingCountry = true;
      
      // Don't sync for 5 seconds after country change
      clearTimeout(syncTimeout);
      syncTimeout = setTimeout(() => {
        isChangingCountry = false;
        console.log('[CartProvider] Auto-sync resumed');
      }, 5000);
    };

    const syncCart = async () => {
      if (isChangingCountry) {
        console.log('[CartProvider] Skipping sync during country change');
        return;
      }
      
      try {
        const serverCart = await cartService.fetchCart();
        console.log('[CartProvider] Syncing cart, items count:', serverCart?.items?.length || 0);
        dispatch({ type: 'SET_CART', payload: serverCart });
      } catch (error) {
        console.error('Failed to sync cart:', error);
        dispatch({ type: 'EMPTY_CART' });
      }
    };

    window.addEventListener('user-locale-changed', handleLocaleChanged);

    // Initial sync
    syncCart();

    // Set up interval for syncing (less frequent during country change)
    const interval = setInterval(() => {
      if (!isChangingCountry) {
        syncCart();
      }
    }, 30000);

    return () => {
      clearInterval(interval);
      clearTimeout(syncTimeout);
      window.removeEventListener('user-locale-changed', handleLocaleChanged);
    };
  }, []);

  useEffect(() => {
    const handleEmptyCart = async () => {
      try {
        console.log('[CartProvider] Empty cart event received - starting cart clearing process');
        
        // Get the current server cart first
        let serverCart;
        try {
          serverCart = await cartService.fetchCart();
          console.log('[CartProvider] Server cart items to remove:', serverCart?.items?.length || 0);
        } catch (fetchError) {
          console.error('[CartProvider] Failed to fetch cart for clearing:', fetchError);
          serverCart = { items: [] };
        }
        
        // Remove all items from server
        if (serverCart?.items && serverCart.items.length > 0) {
          const removalPromises = serverCart.items.map(async (item:any) => {
            try {
              await cartService.removeCartItem(item.uuid);
              console.log('[CartProvider] Successfully removed item:', item.uuid);
              return true;
            } catch (itemError) {
              console.error('[CartProvider] Failed to remove item:', item.uuid, itemError);
              return false;
            }
          });
          
          // Wait for all removals to complete
          const results = await Promise.allSettled(removalPromises);
          const successfulRemovals = results.filter(result => result.status === 'fulfilled').length;
          console.log(`[CartProvider] Removed ${successfulRemovals}/${serverCart.items.length} items from server`);
        }
        
        // Now empty the local cart state
        _dispatch({ type: 'EMPTY_CART' });
        
        // Clear local storage
        localStorage.removeItem('cart-items');
        localStorage.removeItem('cart');
        
        // Dispatch completion event
        window.dispatchEvent(new CustomEvent('cart-emptied'));
        
        console.log('[CartProvider] Cart clearing process completed');
        
      } catch (error) {
        console.error('[CartProvider] Error in handleEmptyCart:', error);
        // Still ensure local state is cleared
        _dispatch({ type: 'EMPTY_CART' });
      }
    };

    window.addEventListener('empty-cart', handleEmptyCart);
    
    return () => {
      window.removeEventListener('empty-cart', handleEmptyCart);
    };
  }, []);

  const dispatch: DispatchType = (actionOrThunk) => {
    if (typeof actionOrThunk === 'function') {
      // ✅ forward the return value
      return actionOrThunk(_dispatch, cart)
    } else {
      _dispatch(actionOrThunk)
    }
  }

  const updateCurrency = (currency: string) => {
    dispatch({ type: 'SET_CURRENCY', payload: currency })
  }

  const syncCart = async () => {
    try {
      const serverCart = await cartService.fetchCart()
      dispatch({ type: 'SET_CART', payload: serverCart })
    } catch (error) {
      console.error('Failed to sync cart:', error)
      dispatch({ type: 'EMPTY_CART' })
    }
  }

  useEffect(() => {
    syncCart()
  }, [])

  return (
    <CartContext.Provider value={{ ...cart, updateCurrency }}>
      <CartDispatchContext.Provider value={dispatch}>
        {children}
      </CartDispatchContext.Provider>
    </CartContext.Provider>
  )
}

export function useCartState() {
  const context = React.useContext(CartContext)
  if (!context) {
    throw new Error('useCartState must be used within a CartProvider')
  }
  return context
}

export function useCartDispatch() {
  const context = React.useContext(CartDispatchContext)
  if (!context) {
    throw new Error('useCartDispatch must be used within a CartProvider')
  }
  return context
}